




























































































import Vue from 'vue';
import {
  required,
  numeric,
  maxValue,
  minValue,
  maxLength,
} from 'vuelidate/lib/validators';
import { ssn } from '@/validations';
import InputMaskTypes from '@/directives/input-mask/input-mask-types';
import { FormValidation } from '@/mixins';
import { ExtendMixins } from '@/services';
import { PRIMARY_BENEFICIARY, CONTINGENT_BENEFICIARY } from '@/constants';
import { AccountBeneficiaryResponseModel, BeneficiaryFormModel, SelectOption } from '@/@types/member';

interface Data {
  beneficiaryModel: BeneficiaryFormModel;
  relationshipOptions: SelectOption[];
}

export default ExtendMixins(
  FormValidation,
).extend({
  name: 'BeneficiaryForm',
  props: {
    beneficiary: {
      type: Object as () => AccountBeneficiaryResponseModel,
      default: null,
    },
    hasPrimaryBeneficiaries: {
      type: Boolean,
      required: true,
    },
    previousScreen: {
      type: String,
      default: 'view',
    },
  },
  data(): Data {
    return {
      beneficiaryModel: {
        firstName: '',
        lastName: '',
        ssn: '',
        type: '',
        relationship: '',
        sharePercentage: null,
      },
      relationshipOptions: [
        {
          text: this.$options.filters.t([
            'beneficiaries_relationship-option_spouse',
            'global_spouse',
          ]),
          value: 'Spouse',
        },
        {
          text: this.$options.filters.t([
            'beneficiaries_relationship-option_child',
            'global_child',
          ]),
          value: 'Child',
        },
        {
          text: this.$options.filters.t([
            'beneficiaries_relationship-option_parent',
            'global_parent',
          ]),
          value: 'Parent',
        },
        {
          text: this.$options.filters.t([
            'beneficiaries_relationship-option_sibling',
            'global_sibling',
          ]),
          value: 'Sibling',
        },
        {
          text: this.$options.filters.t([
            'beneficiaries_relationship-option_other',
            'global_other',
          ]),
          value: 'Other',
        },
      ],
    };
  },
  validations: {
    beneficiaryModel: {
      firstName: {
        required,
        maxLength: maxLength(20),
      },
      lastName: {
        required,
        maxLength: maxLength(30),
      },
      ssn: {
        ssnFormat: ssn.format,
      },
      type: {
        required,
      },
      relationship: {
        required,
      },
      sharePercentage: {
        required,
        numeric,
        minValue: minValue(1),
        maxValue: maxValue(100),
      },
    },
  },
  computed: {
    typeOptions(): SelectOption[] {
      const options = [
        {
          text: this.$options.filters.t([
            'beneficiaries_type-option_primary',
            'global_primary',
          ]),
          value: PRIMARY_BENEFICIARY,
        },
      ];
      if (this.hasPrimaryBeneficiaries) {
        options.push({
          text: this.$options.filters.t([
            'beneficiaries_type-option_secondary',
            'global_secondary',
          ]),
          value: CONTINGENT_BENEFICIARY,
        });
      }
      return options;
    },

    //      VALIDATIONS
    firstNameErrors(): string[] {
      const formField = 'beneficiaryModel.firstName';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'beneficiaries_form-error_first-name_required',
            'global_form-error_first-name_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          'beneficiaries_form-error_first-name_length',
          'maxLength',
          { key: 'maxLength', val: 'max' },
        ),
      ]);
    },
    lastNameErrors(): string[] {
      const formField = 'beneficiaryModel.lastName';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'beneficiaries_form-error_last-name_required',
            'global_form-error_last-name_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          'beneficiaries_form-error_last-name_length',
          'maxLength',
          { key: 'maxLength', val: 'max' },
        ),
      ]);
    },
    ssnErrors(): string[] {
      const formField = 'beneficiaryModel.ssn';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'beneficiaries_form-error_ssn_invalid-format',
            'global_form-error_default_invalid-format',
          ],
          'ssnFormat',
        ),
      ]);
    },
    typeErrors(): string[] {
      const formField = 'beneficiaryModel.type';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'beneficiaries_form-error_type_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
      ]);
    },
    relationshipErrors(): string[] {
      const formField = 'beneficiaryModel.relationship';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'beneficiaries_form-error_relationship_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
      ]);
    },
    sharePercentageErrors(): string[] {
      const formField = 'beneficiaryModel.sharePercentage';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'beneficiaries_form-error_share-percentage_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          [
            'beneficiaries_form-error_share-percentage_invalid-value',
            'global_form-error_default_invalid-format',
          ],
          'numeric',
        ),
        this.FormValidationMixin.validateField(
          formField,
          [
            'beneficiaries_form-error_share-percentage_invalid-max-value',
            'global_form-error_default_invalid-format',
          ],
          'maxValue',
        ),
        this.FormValidationMixin.validateField(
          formField,
          [
            'beneficiaries_form-error_share-percentage_invalid-min-value',
            'global_form-error_default_invalid-format',
          ],
          'minValue',
        ),
      ]);
    },
  },
  beforeMount() {
    if (this.beneficiary) {
      this.setBeneficiaryModel();
    }
    this.constructShowErrorModel('beneficiaryModel');
  },
  methods: {
    deconstructSSN(): void {
      this.beneficiaryModel.ssn = InputMaskTypes.ssn.deconstruct(
        this.beneficiaryModel.ssn,
      );
    },
    deconstructSharePercentage(): void {
      this.beneficiaryModel.sharePercentage = InputMaskTypes.integer.deconstruct(
        this.beneficiaryModel.sharePercentage,
      );
    },
    setBeneficiaryModel(): void {
      this.beneficiaryModel = Object.assign(
        {},
        this.beneficiary as any,
        {
          ssn: InputMaskTypes.ssn.deconstruct(this.beneficiary.ssn),
      });
    },
    cancel(): void {
      if (this.previousScreen === 'view') {
        this.$emit('closeFullscreenWrapper', this.changeMade);
      } else if (this.previousScreen === 'review') {
        this.$emit('showReviewScreen', this.changeMade);
      }
    },
  },
});
